import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';

import GET_API_KEYS_FOR_ACCOUNT from '@/features/develop/gql/api_keys/getApiKeysForAccount.gql';
import { useDefaultApiKey } from '@/features/develop/hooks/useDefaultApiKey';
import { LoadingSelectorField, NoticeSelectorField } from '@/components/SelectorFieldStates';
import { PaginatedSelectorInputField } from '@repo/ui/Form';
import type { Mercury } from '@repo/types/Models';

const PAGE_SIZE = 5;

export type SelectorOption = { label: string; value: string };

interface ApiKeySelectorFieldProps {
  accountId: string;
  value: SelectorOption | undefined;
  onChange: (value: SelectorOption) => void;
  error?: { message?: string };
  label?: string;
  id?: string;
  /** Called with true when the account has no API keys at all. */
  onEmptyChange?: (isEmpty: boolean) => void;
  /**
   * Fill an empty value with the default (oldest) API key. On for assigning new
   * numbers; off when editing an existing number, so "no API key" stays empty.
   */
  preselectDefault?: boolean;
}

const toOption = (node: { id: string; name: string }, defaultId?: string): SelectorOption => ({
  label: node.id === defaultId ? `${node.name} (default)` : node.name,
  value: node.id,
});

/**
 * API key picker for one account. Always a dropdown; the oldest key is the
 * default and is preselected when nothing is chosen yet.
 */
export const ApiKeySelectorField = ({
  accountId,
  value,
  onChange,
  error,
  label = 'API Key',
  id = 'api_key_id',
  onEmptyChange,
  preselectDefault = true,
}: ApiKeySelectorFieldProps) => {
  const [queryVars, setQueryVars] = useState<Mercury.GQL.QueryVariables>({ first: PAGE_SIZE });

  useEffect(() => {
    setQueryVars({ first: PAGE_SIZE });
  }, [accountId]);

  const {
    defaultApiKey,
    loading: defaultLoading,
    error: defaultError,
  } = useDefaultApiKey(accountId);

  const {
    loading,
    data,
    error: queryError,
  } = useQuery(GET_API_KEYS_FOR_ACCOUNT, {
    variables: { id: accountId, ...queryVars },
    notifyOnNetworkStatusChange: true,
  });

  const options: SelectorOption[] =
    data?.apiKeysForAccount?.edges.map(({ node }) => toOption(node, defaultApiKey?.id)) ?? [];
  const pageInfo = data?.apiKeysForAccount?.pageInfo;
  const isOnlyPage = !pageInfo?.hasPreviousPage && !pageInfo?.hasNextPage;
  const isSearching = !!queryVars.query;

  const loadFailed = !!(queryError || defaultError);
  // Only a successful, empty, unfiltered result means the account has no keys
  const hasNone = !loading && !loadFailed && options.length === 0 && isOnlyPage && !isSearching;

  useEffect(() => {
    onEmptyChange?.(hasNone);
  }, [hasNone, onEmptyChange]);

  useEffect(() => {
    if (preselectDefault && defaultApiKey && !value?.value) {
      onChange(toOption(defaultApiKey, defaultApiKey.id));
    }
  }, [preselectDefault, defaultApiKey, value?.value, onChange]);

  if ((loading && !data) || defaultLoading) return <LoadingSelectorField label={label} />;

  if (loadFailed) {
    if (queryError) console.error(queryError);
    if (defaultError) console.error(defaultError);
    return (
      <NoticeSelectorField label={label}>
        Couldn&apos;t load API keys for this account. Please refresh and try again.
      </NoticeSelectorField>
    );
  }

  if (hasNone) {
    return (
      <NoticeSelectorField label={label}>
        No API Keys found for this account. Please create an API Key first.
      </NoticeSelectorField>
    );
  }

  return (
    <PaginatedSelectorInputField
      loading={loading}
      id={id}
      label={label}
      onChange={(selected) => onChange(selected as unknown as SelectorOption)}
      value={value}
      error={error}
      options={options}
      pageInfo={pageInfo}
      setQueryVars={setQueryVars}
      pageSize={PAGE_SIZE}
      name="api_key"
    />
  );
};
